import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';
import { ConfigService } from 'src/app/services/config.service';
import { IEditableContent } from 'src/app/interfaces/IEditableContent';

@Component({
  selector: 'app-page-lc-summary',
  templateUrl: './page-lc-summary.component.html',
  styleUrls: ['./page-lc-summary.component.scss'],
})
export class PageLcSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();
  public electionStatusString = '';
  public disclaimer = '';
  public leadingByDistrictTitle = 'Candidates currently leading in districts';
  public bodyHeight: string = '' + (window.innerHeight - 110) + 'px';

  public cols = 2;
  public rowHeight = 90;
  public pageTitle = '';
  public headingTitle = 'Legislative Council Results';
  public lastUpdate: Date = new Date();
  public isFinal: boolean = false;
  public isMobile = false;
  public editableContent: IEditableContent | null = null;

  constructor(
    public electionService: ElectionService,
    private changeDetector: ChangeDetectorRef,
    private deviceService: DeviceDetectorService,
    private configService: ConfigService
  ) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((dataLoaded: any) => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.loadPage(this.electionService.selectedPage);
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((page: ISelectPage) => {
        this.loadPage(page);
      });
  }

  ngOnInit() {
    this.checkResize();
    fromEvent(window, 'resize').subscribe((e: any) => {
      this.checkResize();
    });

    // Load editable content
    this.configService
      .getLCSummaryEditableContent$()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((content: IEditableContent | null) => {
        this.editableContent = content;
        this.changeDetector.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    if (
      this.isDataLoaded(
        this.electionService.selectedPage.electionDateString ?? ''
      )
    ) {
      this.loadPage(this.electionService.selectedPage);
    }
  }

  private doResize(): void {
    const wid = window.innerWidth;
    if (wid < 800) {
      this.cols = 1;
    } else {
      this.cols = 2;
    }
    this.bodyHeight = '' + (window.innerHeight - 110) + 'px';
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  private loadPage(page: ISelectPage): void {
    this.lastUpdate = this.electionService.getLastUpdate(
      page.electionDateString ?? '',
      false
    );

    // check if election results are final
    const election = this.electionService.electionMap.get(
      page.electionDateString ?? ''
    );
    // Use lcChange for Legislative Council election status
    this.isFinal =
      election?.lcChange?.electionStatus === ElectionStatusEnum.final;

    if (this.isFinal) {
      this.headingTitle = 'Legislative Council Results \u2013 Final';
      this.disclaimer = 'The counting of votes for this election is now concluded. All votes have been counted and the results are final.';
    } else {
      this.headingTitle = 'Legislative Council Results \u2013 Provisional';
      this.disclaimer = 'Please note, results are provisional. Results will be published progressively. The figures below may not include all votes.';
    }

    this.changeDetector.detectChanges();
  }

  private checkResize(): void {
    this.isMobile = this.deviceService.isMobile();
    this.electionService.isMobile = this.isMobile;
  }

  public ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
